'use client';

import { Button } from '@/components/button';
import classes from '@/components/calculator/CalculatorBase.module.scss';
import type { CalculatorBaseProps } from '@/components/calculator/CalculatorBase.types';
import {
  buildConsumptionEndAdornment,
  getCalculatorConsumptionAriaLabel,
  getCalculatorConsumptionFieldLabel,
} from '@/components/calculator/calculatorConsumptionFields';
import {
  getCalculatorDefaultConsumptionNtString,
  getCalculatorDefaultConsumptionString,
  resolveCalculatorDefaultSelectedStep,
} from '@/components/calculator/calculatorDefaults';
import { resolveCalculatorTargetGroup } from '@/components/calculator/resolveCalculatorTargetGroup';
import SubmitWrapper from '@/components/helperTextWrapper/SubmitWrapper';
import IconSelectCalculator from '@/components/iconSelect/IconSelectCalculator';
import { TextField } from '@/components/textField';
import { ProductCategory, TargetGroup } from '@/helper/enums';
import { useSettings } from '@/providers/SettingsProvider';
import type { GlobalProjectSettingsStoryblok, PartialStepSettingStoryblok } from '@/types/component-types-sb';
import type { FormikErrors, FormikTouched } from 'formik';
import type { TFunction } from 'i18next';
import type { Dispatch, FC, FormEvent, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

const noop = (): void => {};

export const calculatorNoopSetSelectedStep: Dispatch<
  SetStateAction<PartialStepSettingStoryblok | undefined>
> = () => {};

const emptyFormikErrors = {} as FormikErrors<unknown>;
const emptyFormikTouched = {} as FormikTouched<unknown>;

export type CalculatorFormViewProps = {
  type: ProductCategory;
  button?: string;
  heatflow?: boolean;
  targetGroup: TargetGroup;
  ready: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  postcode: {
    value: string;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    onBlur: React.FocusEventHandler<HTMLInputElement>;
    error: boolean;
    helperText?: string | false;
    inputProps: { maxLength: number; inputMode?: 'numeric'; 'aria-label': string };
    endAdornment: React.ReactNode;
  };
  consumption: {
    value: string;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    onBlur: React.FocusEventHandler<HTMLInputElement>;
    error: boolean;
    helperText?: string | false;
    inputProps: { maxLength: number; 'aria-label': string };
    endAdornment: React.ReactNode;
  };
  consumptionNt?: {
    value: string;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    onBlur: React.FocusEventHandler<HTMLInputElement>;
    error: boolean;
    helperText?: string | false;
    inputProps: { maxLength: number; 'aria-label': string };
    endAdornment: React.ReactNode;
  };
  selectedStep: PartialStepSettingStoryblok | undefined;
  setSelectedStep: Dispatch<SetStateAction<PartialStepSettingStoryblok | undefined>>;
  submitDisabled: boolean;
  formikErrors: FormikErrors<unknown>;
  formikTouched: FormikTouched<unknown>;
};

export function getCalculatorFormViewStaticProps(
  props: CalculatorBaseProps,
  pathname: string | null | undefined,
  t: TFunction,
  settings?: GlobalProjectSettingsStoryblok | null
): CalculatorFormViewProps {
  const targetGroup = resolveCalculatorTargetGroup(pathname, props.targetGroup);
  const { heatflow } = props;
  const defaultConsumption = getCalculatorDefaultConsumptionString(settings, props.type, heatflow, targetGroup);
  const defaultConsumptionNt = getCalculatorDefaultConsumptionNtString(settings, heatflow);
  const defaultSelectedStep = resolveCalculatorDefaultSelectedStep(settings, props.type, heatflow, targetGroup);
  return {
    type: props.type,
    button: props.button,
    heatflow,
    targetGroup,
    ready: false,
    onSubmit: (e: FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
    },
    postcode: {
      value: '',
      onChange: noop,
      onBlur: noop,
      error: false,
      inputProps: { maxLength: 5, inputMode: 'numeric', 'aria-label': '' },
      endAdornment: <span aria-hidden={true} />,
    },
    consumption: {
      value: defaultConsumption,
      onChange: noop,
      onBlur: noop,
      error: false,
      inputProps: {
        maxLength: 7,
        'aria-label': getCalculatorConsumptionAriaLabel(t),
      },
      endAdornment: heatflow
        ? buildConsumptionEndAdornment('ht', settings, t)
        : buildConsumptionEndAdornment('standard', settings, t),
    },
    consumptionNt: heatflow
      ? {
          value: defaultConsumptionNt,
          onChange: noop,
          onBlur: noop,
          error: false,
          inputProps: { maxLength: 7, 'aria-label': getCalculatorConsumptionAriaLabel(t) },
          endAdornment: buildConsumptionEndAdornment('nt', settings, t),
        }
      : undefined,
    selectedStep: defaultSelectedStep,
    setSelectedStep: calculatorNoopSetSelectedStep,
    submitDisabled: false,
    formikErrors: emptyFormikErrors,
    formikTouched: emptyFormikTouched,
  };
}

const CalculatorFormView: FC<CalculatorFormViewProps> = ({
  type,
  button,
  heatflow,
  targetGroup,
  ready,
  onSubmit,
  postcode,
  consumption,
  consumptionNt,
  selectedStep,
  setSelectedStep,
  submitDisabled,
  formikErrors,
  formikTouched,
}) => {
  const { t } = useTranslation();
  const settings = useSettings();
  const showIconSelect = !heatflow && targetGroup === TargetGroup.PK;

  const consumptionLabel = getCalculatorConsumptionFieldLabel(heatflow ? 'ht' : 'standard', t);
  const consumptionInputProps = heatflow
    ? { ...consumption.inputProps, 'aria-label': getCalculatorConsumptionAriaLabel(t) }
    : consumption.inputProps;
  const consumptionEndAdornment = heatflow ? buildConsumptionEndAdornment('ht', settings, t) : consumption.endAdornment;

  const consumptionNtInputProps =
    consumptionNt && heatflow
      ? { ...consumptionNt.inputProps, 'aria-label': getCalculatorConsumptionAriaLabel(t) }
      : consumptionNt?.inputProps;
  const consumptionNtEndAdornment =
    consumptionNt && heatflow ? buildConsumptionEndAdornment('nt', settings, t) : consumptionNt?.endAdornment;

  return (
    <div className={classes.calculator}>
      <form
        noValidate
        data-testid="CalculatorBaseForm"
        data-calculator-ready={ready ? 'true' : 'false'}
        onSubmit={onSubmit}
      >
        <div className={classes.innerWrapper}>
          <TextField
            data-testid="postcode-input"
            required
            name="postcode"
            size="small"
            fullWidth
            label={t('Postleitzahl')}
            onChange={postcode.onChange}
            onBlur={postcode.onBlur}
            value={postcode.value}
            error={postcode.error}
            helperText={postcode.helperText}
            inputProps={postcode.inputProps}
            InputProps={{
              endAdornment: postcode.endAdornment,
              inputMode: 'numeric',
            }}
          />
        </div>
        {showIconSelect && (
          <div className={classes.iconSelect}>
            <IconSelectCalculator
              label={type === ProductCategory.Electricity ? t('iconSelect.persons') : t('iconSelect.qm')}
              type={type}
              selected={selectedStep}
              setSelectedStep={setSelectedStep}
            />
          </div>
        )}
        <div className={classes.innerWrapper}>
          <TextField
            data-testid="consumption-input"
            required
            name="consumption"
            size="small"
            fullWidth
            label={consumptionLabel}
            value={consumption.value}
            onChange={consumption.onChange}
            onBlur={consumption.onBlur}
            error={consumption.error}
            helperText={consumption.helperText}
            InputProps={{
              endAdornment: consumptionEndAdornment,
              inputMode: 'numeric',
            }}
            inputProps={consumptionInputProps}
          />
        </div>
        {heatflow && consumptionNt && (
          <TextField
            data-testid="consumptionNt-input"
            required
            name="consumptionNt"
            size="small"
            fullWidth
            label={getCalculatorConsumptionFieldLabel('nt', t)}
            value={consumptionNt.value}
            onChange={consumptionNt.onChange}
            onBlur={consumptionNt.onBlur}
            error={consumptionNt.error}
            helperText={consumptionNt.helperText}
            InputProps={{
              endAdornment: consumptionNtEndAdornment,
              inputMode: 'numeric',
            }}
            inputProps={consumptionNtInputProps}
          />
        )}
        <SubmitWrapper customClassName={classes.submitButton} formikErrors={formikErrors} formikTouched={formikTouched}>
          <Button type="submit" variant="ctaFull" disabled={submitDisabled}>
            {button ? button : t('Tarif finden')}
          </Button>
        </SubmitWrapper>
      </form>
    </div>
  );
};

export default CalculatorFormView;
